import { useState, useEffect, useMemo } from "react";
import {
  Card,
  Text,
  Title,
  BarChart,
} from "@tremor/react";
import { PALETTE } from "../lib/palette";
import DistribucionCategoriaSection from "./DistribucionCategoriaSection";
import { apiFetch } from "../api/client";

// ── Interfaces ──────────────────────────────────────────

interface ProductoKPI {
  producto: string;
  categoria: string;
  venta_ytd: number;
  pct_total: number;
  venta_mes_actual: number | null;
  mom_pct: number | null;
  min_mes: number;
  max_mes: number;
  promedio_mes: number;
  tendencia: "creciente" | "estable" | "decreciente";
}

interface VentasPorProductoResponse {
  restaurant_id: string;
  anio: number;
  meses_cargados: number[];
  venta_ytd: number;
  venta_mes_actual: number | null;
  mom_pct: number | null;
  n_productos_activos: number;
  productos: ProductoKPI[];
}

// ── Colores y helpers ───────────────────────────────────

const TENDENCIA_BADGE: Record<string, { icon: string; cls: string }> = {
  creciente: { icon: "▲", cls: "text-emerald-600" },
  estable: { icon: "→", cls: "text-zinc-500" },
  decreciente: { icon: "▼", cls: "text-red-600" },
};

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 0 }).format(n);

const formatPct = (n: number | null | undefined) =>
  n == null ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;

const MESES_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const mesesLabel = (mm: number) => MESES_ES[mm - 1] ?? String(mm);

// ── Componente ──────────────────────────────────────────

interface Props {
  restaurantId: string;
  mesSel: { anio: number; mm: number } | null;
  anio?: number;
}

export default function VentasPorProductoSection({ restaurantId, mesSel, anio }: Props) {
  const [data, setData] = useState<VentasPorProductoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch ventas por producto
  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ restaurant_id: restaurantId, limit: "32" });
        if (mesSel) {
          params.set("anio", String(mesSel.anio));
          params.set("mm", String(mesSel.mm));
        } else if (anio) {
          params.set("anio", String(anio));
        }
        const res = await apiFetch(`/api/comercial/ventas-por-producto?${params}`);
        if (!res.ok) {
          if (!cancelled) {
            // 404 = restaurante sin datos de producto → no es un error de red.
            setError(res.status === 404 ? "SIN_DATOS" : `HTTP ${res.status}`);
            setLoading(false);
          }
          return;
        }
        const body: VentasPorProductoResponse = await res.json();
        if (!cancelled) { setData(body); setLoading(false); }
      } catch (err) {
        if (!cancelled) { setError((err as Error).message); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId, mesSel, anio]);


  // Concentración: top-5 / top-10 sobre el total YTD (calculado localmente).
  const concentracion = useMemo(() => {
    if (!data || data.productos.length === 0) return { top5: null as number | null, top10: null as number | null };
    const total = data.productos.reduce((s, p) => s + p.venta_ytd, 0);
    if (total <= 0) return { top5: null, top10: null };
    const sorted = [...data.productos].sort((a, b) => b.venta_ytd - a.venta_ytd);
    const sumN = (n: number) => sorted.slice(0, n).reduce((s, p) => s + p.venta_ytd, 0);
    return {
      top5: Math.round((sumN(5) / total) * 1000) / 10,
      top10: Math.round((sumN(10) / total) * 1000) / 10,
    };
  }, [data]);

  if (loading) {
    return (
      <Card className="!p-3">
        <Text>Cargando ventas por producto...</Text>
      </Card>
    );
  }

  if (error || !data) {
    if (error === "SIN_DATOS") {
      return (
        <Card className="!p-3">
          <Text className="text-tremor-content">Sin datos de ventas por producto para este restaurante.</Text>
        </Card>
      );
    }
    return (
      <Card className="!p-3">
        <Text className="text-red-600">Error: {error ?? "Sin datos"}</Text>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <Title className="!text-base">📦 Ventas por Producto</Title>
        <Text className="!text-xs">
          {mesSel
            ? `${data.anio} Acumulado enero–${mesesLabel(mesSel.mm)} · ${data.n_productos_activos} productos`
            : `${data.anio} YTD · ${data.n_productos_activos} productos · ${data.meses_cargados.length} meses`}
        </Text>
      </div>

      {/* Row 2: Distribución por categoría (izq) + Top 10 (centro) + Top 5 más vendidos (der) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
        <DistribucionCategoriaSection />

        <Card className="!p-3">
          <Title className="!text-base">Top 10 productos</Title>
          <Text className="!text-xs">Ventas YTD por producto</Text>
          <BarChart
            className="mt-3 h-72"
            data={data.productos.slice(0, 10).map((p) => ({
              name: p.producto.replace(/^(Alimentos|Bebidas Sin Alcohol|Bebidas con Alcohol|Vinos y Espumantes|Productos Retail)\s*-\s*/, ""),
              Venta: p.venta_ytd,
            }))}
            index="name"
            categories={["Venta"]}
            colors={[PALETTE.primaryDark]}
            valueFormatter={formatCurrency}
            yAxisWidth={80}
            layout="vertical"
            showLegend={false}
          />
        </Card>

        <Card className="!p-3">
          <Title className="!text-base">🏆 Top 5 productos más vendidos</Title>
          <Text className="!text-xs">Por venta acumulada (S/)</Text>
          {data.productos.slice(0, 5).length > 0 ? (
            <div className="mt-2 space-y-1">
              {data.productos.slice(0, 5).map((p, i) => (
                <div key={`top-${i}`} className="flex items-center justify-between text-sm border-b border-tremor-border pb-1">
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {i + 1}. {p.producto.replace(/^(Alimentos|Bebidas Sin Alcohol|Bebidas con Alcohol|Vinos y Espumantes|Productos Retail)\s*-\s*/, "")}
                    </div>
                    <div className="text-[10px] text-tremor-content">{p.categoria}</div>
                  </div>
                  <div className="text-right tabular-nums">
                    <div className="font-semibold">{formatCurrency(p.venta_ytd)}</div>
                    <div className="text-[10px] text-tremor-content">
                      {p.pct_total != null ? `${p.pct_total.toFixed(1)}%` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Text className="!text-xs text-tremor-content mt-2">Sin datos de productos.</Text>
          )}
          <Text className="!text-[10px] mt-2 text-tremor-content">
            {concentracion.top5 != null ? `Top 5 concentra ${concentracion.top5}% de las ventas` : ""}
          </Text>
        </Card>
      </div>


      {/* Row 5: Tabla detallada por categoría (cuadros independientes) */}
      {(() => {
        const cats = Array.from(new Set(data.productos.map((p) => p.categoria)));
        return (
          <div className="space-y-3">
            <div>
              <Title className="!text-base">📋 Todos los productos</Title>
              <Text className="!text-xs">Ordenados por venta YTD descendente · agrupados por categoría</Text>
            </div>
            {cats.map((cat) => {
              const prods = data.productos.filter((p) => p.categoria === cat);
              return (
                <Card key={cat} className="!p-3">
                  <Title className="!text-sm mb-2">{cat}</Title>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-tremor-border">
                          <th className="text-left py-1.5 pr-3 text-tremor-content font-medium">#</th>
                          <th className="text-left py-1.5 pr-3 text-tremor-content font-medium">Producto</th>
                          <th className="text-right py-1.5 pr-3 text-tremor-content font-medium">Venta YTD</th>
                          <th className="text-right py-1.5 pr-3 text-tremor-content font-medium">%</th>
                          <th className="text-right py-1.5 pr-3 text-tremor-content font-medium">Mes actual</th>
                          <th className="text-right py-1.5 pr-3 text-tremor-content font-medium">MoM</th>
                          <th className="text-center py-1.5 text-tremor-content font-medium">Tendencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prods.map((p, i) => {
                          const t = TENDENCIA_BADGE[p.tendencia] ?? TENDENCIA_BADGE.estable;
                          return (
                            <tr key={p.producto} className="border-b border-tremor-border/50 hover:bg-tremor-background-subtle">
                              <td className="py-1.5 pr-3 text-tremor-content">{i + 1}</td>
                              <td className="py-1.5 pr-3 font-medium text-tremor-content-emphasis truncate max-w-[200px]" title={p.producto}>
                                {p.producto}
                              </td>
                              <td className="py-1.5 pr-3 text-right font-medium">{formatCurrency(p.venta_ytd)}</td>
                              <td className="py-1.5 pr-3 text-right">{p.pct_total.toFixed(1)}%</td>
                              <td className="py-1.5 pr-3 text-right">
                                {p.venta_mes_actual ? formatCurrency(p.venta_mes_actual) : "—"}
                              </td>
                              <td className="py-1.5 pr-3 text-right">
                                {p.mom_pct !== null ? (
                                  <span className={p.mom_pct >= 0 ? "text-emerald-600" : "text-red-600"}>
                                    {formatPct(p.mom_pct)}
                                  </span>
                                ) : "—"}
                              </td>
                              <td className="py-1.5 text-center">
                                <span className={t.cls} title={p.tendencia}>{t.icon}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
